'use client'
import { useTreeSlice } from '~/hooks/use-tree-slice'
import { useWorkingTreeStore } from '../blocks-tree/provider'
import { Block, MediaBlock } from '@basehub/data/replicache/index'
import { useMemo } from 'react'

export type RepoFile = {
  key: string
  fileName: string
  size: string
  lastModified?: Date
  altText?: string | null
  duration?: number | null
  url: string
} & (
  | { type: 'file' | 'audio' }
  | { type: 'video' | 'image'; width: number; height: number }
)

// TODO: Hash results and use them to avoid recomputing the same files
export const useRepoFiles = () => {
  const tree = useWorkingTreeStore((s) => s.tree)
  const mediaUploadedBlocks = useTreeSlice(tree, (s) => {
    const mediaBlockIds =
      (s.compoundIndexes.get('blocksByType') as Map<Block['type'], Set<string>>)
        .get('media')
        ?.values() ?? []
    const mediaBlocks: MediaBlock[] = []
    for (const bId of mediaBlockIds) {
      const block = s.getNode(bId)?.value
      if (!block) continue
      if (block.type === 'media' && block.value.status === 'uploaded') {
        mediaBlocks.push(block)
      }
    }
    return mediaBlocks
  })

  return useMemo<RepoFile[] | null>(() => {
    const mediaByUrl = new Map<string, RepoFile>()
    for (const b of mediaUploadedBlocks ?? []) {
      if (b.value.status !== 'uploaded') continue
      const assetURL = b.value.url.replace(
        'basehub.earth',
        'assets.basehub.com'
      )
      if (mediaByUrl.has(assetURL)) continue

      const size = b.value.fileSize
        ? `${
            b.value.fileSize >= 1024 * 1024
              ? `${Math.round((b.value.fileSize / 1024 / 1024) * 10) / 10} MB`
              : `${Math.round((b.value.fileSize / 1024) * 10) / 10} KB`
          }`
        : ''

      switch (b.value.type) {
        case 'video':
        case 'image':
          mediaByUrl.set(assetURL, {
            key: assetURL,
            fileName: b.value.fileName,
            type: b.value.type,
            width: b.value.width,
            height: b.value.height,
            url: assetURL,
            size,
            altText: 'altText' in b.value ? b.value.altText : undefined,
          })
          break
        case 'audio':
          mediaByUrl.set(assetURL, {
            key: assetURL,
            fileName: b.value.fileName,
            type: b.value.type,
            duration: b.value.duration,
            url: assetURL,
            size,
          })
          break
        default:
          mediaByUrl.set(assetURL, {
            key: assetURL,
            fileName: b.value.fileName,
            type: b.value.type,
            url: assetURL,
            size,
          })
      }
    }

    return Array.from(mediaByUrl.values())
  }, [mediaUploadedBlocks])
}
